import React from "react";
import { useTranslation } from "react-i18next";
import { useAdminControls } from "../../state/AdminControlsContext";
import { useAdminService } from "../../data/adminService";
import { useAsyncData } from "../../hooks/useAsyncData";
import { formatShortDate, formatTimeDe } from "../../utils/format";
import { KpiGrid } from "./components/KpiGrid";
import { DashboardCharts } from "./components/DashboardCharts";
import { ImportOriginCard } from "./components/ImportOriginCard";
import { FeatureUsageSection } from "./components/FeatureUsageSection";
import { MilestonesCard } from "./components/MilestonesCard";
import "./dashboard.css";

/**
 * Dashboard: header + 4 KPI cards (FE-2), the series charts (FE-3), and the
 * milestones card (FE-4), stacked below each other and all driven by the Topbar
 * time-range (Tag/Woche/Monat + custom Von/Bis) via AdminControls.
 */
export const DashboardScreen: React.FC = () => {
  const { t } = useTranslation();
  const { timeRange, customFrom, customTo, rangeVars } = useAdminControls();
  const service = useAdminService();

  const { data, loading, error, reload } = useAsyncData(
    () => service.getDashboardKpis(rangeVars),
    [service, rangeVars.range, rangeVars.from, rangeVars.to],
  );

  const customSpan =
    customFrom && customTo ? `${formatShortDate(customFrom)} – ${formatShortDate(customTo)}` : null;

  // Long label for the header subtitle (Heute / Diese Woche / Dieser Monat / span).
  const rangeLabel =
    timeRange === "CUSTOM" && customSpan
      ? customSpan
      : t(`dashboard.rangeLabel.${timeRange.toLowerCase()}`);

  // Short label for the "Neue User · {range}" KPI (Tag / Woche / Monat / span).
  const rangeShort =
    timeRange === "CUSTOM" && customSpan
      ? customSpan
      : t(`topbar.range.${timeRange.toLowerCase()}`);

  const asOf = t("dashboard.asOf", { time: formatTimeDe(new Date()) });

  return (
    <div className="shell-content-inner">
      <div className="view-header dashboard-header">
        <div>
          <h1 className="view-title">{t("dashboard.title")}</h1>
          <p className="view-subtitle">
            {t("dashboard.subtitlePrefix")}{" "}
            <span className="dashboard-range-label">{rangeLabel}</span>
          </p>
        </div>
        <span className="dashboard-asof">{asOf}</span>
      </div>

      {error ? (
        <div className="lyra-error">
          <span>{t("dashboard.error")}</span>
          <button className="range-btn range-btn--ghost" onClick={reload}>
            {t("dashboard.retry")}
          </button>
        </div>
      ) : (
        <>
          <KpiGrid data={data} loading={loading} rangeShort={rangeShort} />
          <DashboardCharts />
          <ImportOriginCard />
          {/* Its own labelled block, not a KPI tile: complete figures above,
              a sample below (Masterliste 7.9). */}
          <FeatureUsageSection />
          <MilestonesCard totalUsers={data?.totalUsers ?? null} loading={loading} />
        </>
      )}
    </div>
  );
};
